import { memo, useEffect, useState } from 'react';
import type {
  CollisionDetection,
  DragEndEvent,
  DragStartEvent,
  UniqueIdentifier
} from '@dnd-kit/core';
import {
  closestCorners,
  DndContext,
  DragOverlay,
  MeasuringStrategy,
  pointerWithin
} from '@dnd-kit/core';
import { arrayMove, SortableContext } from '@dnd-kit/sortable';

import {
  type SearchView,
  type SearchViewGroup,
  type SearchViewGroupColumn
} from '@/types/schema/views/SearchView';
import { useDndUtils } from '@/hooks/useDndUtils';
import { getSearchInputsLayoutClassNames } from '@/components/views/search/search-options/SearchInputs';
import { usePageEditorContext } from '@/pages/page/page-editor/PageEditorContext';
import { SearchInputDragOverlayContent } from './SearchInputDragOverlayContent';
import { SearchInputsDroppableColumn } from './SearchInputsDroppableColumn';

interface SearchInputsSortableProps {
  view: SearchView;
}

function SearchInputsSortable({ view }: SearchInputsSortableProps) {
  const { updatePage, setIsDraggingActive } = usePageEditorContext();
  const { optimizedSensors } = useDndUtils();

  const [sortableGroups, setSortableGroups] = useState<SearchViewGroup[]>(view.groups);

  // State for keeping track of the id of the input being dragged. Needed for rendering the dragging overlay
  const [beingDraggedInputId, setBeingDraggedInputId] = useState<UniqueIdentifier | null>(null);

  // Custom collision detection strategy optimized for multiple containers
  const collisionDetectionStrategy: CollisionDetection = (args) => {
    const droppableContainers = args.droppableContainers.filter((droppableContainer) => {
      if (droppableContainer.data.current?.type === 'input') {
        // If the droppable input is the same as the active input, we only want it if it's being dragged within same column
        if (droppableContainer.id === args.active.id) {
          return (
            droppableContainer.data.current?.sortable.containerId ===
            args.active.data.current?.sortable.containerId
          );
        }

        return true;
      }

      // If the droppable container is a column, we only want it if it's empty
      if (droppableContainer.data.current?.type === 'column') {
        return droppableContainer.data.current.isEmpty;
      }

      return true;
    });

    // First, let's see if there are any collisions with the pointer
    const pointerCollisions = pointerWithin({
      ...args,
      droppableContainers
    });

    // If there are collisions with the pointer, return them
    if (pointerCollisions.length > 0) {
      return pointerCollisions;
    }

    // Otherwise, return a closestCorners collision detection
    return closestCorners({
      ...args,
      droppableContainers
    });
  };

  const resetDraggingState = () => {
    setBeingDraggedInputId(null);
    setIsDraggingActive(false);
  };

  const handleDragStart = ({ active }: DragStartEvent) => {
    setBeingDraggedInputId(active.id);
    setIsDraggingActive(true);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!active || !over) {
      return;
    }

    let updatedGroups = sortableGroups;

    const activeColumnId = active.data.current?.sortable.containerId;

    // If the element being dragged over is not an input, it means we are dragging an input over an empty column
    const overColumnId =
      over.data.current?.type === 'input' ? over.data.current?.sortable.containerId : over.id;

    if (!activeColumnId || !overColumnId) {
      return;
    }

    const activeColumnGroup = sortableGroups.find((s) =>
      s.columns.some((c) => c.id === activeColumnId)
    );

    // If the input is being dragged within the same column, we can just update the input order within the column. There is no need to check for the other groups.
    if (activeColumnId === overColumnId) {
      updatedGroups = sortableGroups.map((group) => {
        if (group.id === activeColumnGroup?.id) {
          return {
            ...group,
            columns: group.columns.map((column) => {
              if (column.id === activeColumnId) {
                const oldIndex = column.fields.findIndex((input) => input.id === active.id);
                const newIndex = column.fields.findIndex((input) => input.id === over.id);

                return {
                  ...column,
                  fields: arrayMove(column.fields, oldIndex, newIndex)
                } satisfies SearchViewGroupColumn;
              }
              return column;
            })
          } satisfies SearchViewGroup;
        }

        return group;
      });
    } else {
      const activeColumn = activeColumnGroup?.columns.find(
        (column) => column.id === activeColumnId
      );
      const activeInput = activeColumn?.fields.find((input) => input.id === active.id);

      if (activeInput) {
        // At this point we know that the input is being dragged from one column to another
        updatedGroups = sortableGroups.map((group) => ({
          ...group,
          columns: group.columns.map((column) => {
            // Remove the input from the active column
            if (column.id === activeColumnId) {
              return {
                ...column,
                fields: column.fields.filter((input) => input.id !== active.id)
              } satisfies SearchViewGroupColumn;
            }

            // Add the input to the new column
            if (column.id === overColumnId) {
              // If the column is empty, add the input as the only item
              if (column.fields.length === 0) {
                return {
                  ...column,
                  fields: [activeInput]
                } satisfies SearchViewGroupColumn;
              }

              const existingInputIndex = column.fields.findIndex((input) => input.id === over.id);

              // Determine if the input being dragged should be placed above or below the input it is being dragged over.
              // We only need to do this if the over input is the first one in the column, otherwise it will be placed below
              const isBelowInput =
                existingInputIndex === 0
                  ? over &&
                    active.rect.current.translated &&
                    active.rect.current.translated.top > over.rect.top + over.rect.height / 2
                  : true;
              const modifier = isBelowInput ? 1 : 0;
              const newIndex =
                existingInputIndex >= 0 ? existingInputIndex + modifier : column.fields.length + 1;

              return {
                ...column,
                fields: [
                  ...column.fields.slice(0, newIndex),
                  activeInput,
                  ...column.fields.slice(newIndex, column.fields.length)
                ]
              } satisfies SearchViewGroupColumn;
            }
            return column;
          })
        }));
      }
    }

    // Update the page with the updated groups
    updatePage({
      type: 'view',
      origin: 'live-app',
      action: 'update',
      updatedView: {
        ...view,
        groups: updatedGroups
      }
    });

    // Update the local items state with the updated groups for immediate UI update
    setSortableGroups(updatedGroups);

    resetDraggingState();
  };

  useEffect(() => {
    // Since the groups can change from outside this dnd/sortable container (e.g. adding/deleting an input from the page editor),
    // we need to update the sortable items whenever the view changes to keep the order in sync
    setSortableGroups(view.groups);
  }, [view.groups]);

  return (
    <DndContext
      sensors={optimizedSensors}
      collisionDetection={collisionDetectionStrategy}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={resetDraggingState}
      measuring={{
        droppable: {
          strategy: MeasuringStrategy.BeforeDragging
        }
      }}
    >
      <SortableContext items={sortableGroups.map((item) => item.id)}>
        {sortableGroups.map((group) => (
          <div
            key={group.id}
            className={getSearchInputsLayoutClassNames(view.groups[0].columns.length)}
          >
            {group.columns.map((column) => (
              <SearchInputsDroppableColumn key={column.id} column={column} />
            ))}
          </div>
        ))}
      </SortableContext>

      <DragOverlay>
        <SearchInputDragOverlayContent
          groups={sortableGroups}
          beingDraggedInputId={beingDraggedInputId}
        />
      </DragOverlay>
    </DndContext>
  );
}

const memoized = memo(SearchInputsSortable);
export { memoized as SearchInputsSortable };
